import React, { useState, useEffect } from 'react';
import { detectDetailedSystem, SystemInfo } from '../utils/systemDetection';

interface DownloadButtonProps {
  variant?: 'default' | 'hero';
}

const DownloadButton: React.FC<DownloadButtonProps> = ({ variant = 'default' }) => {
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    const info = detectDetailedSystem();
    setSystemInfo(info);
    
    // 在开发环境下，将检测函数添加到全局作用域以便测试
    if (process.env.NODE_ENV === 'development') {
      (window as any).testSystemDetection = () => {
        console.log('=== 系统检测测试 ===');
        console.log('检测结果:', info);
        console.log('用户代理:', navigator.userAgent);
        console.log('平台信息:', navigator.platform);
        console.log('硬件并发数:', navigator.hardwareConcurrency);
        console.log('设备像素比:', window.devicePixelRatio);
        return info;
      };
      console.log('系统检测完成，在控制台运行 testSystemDetection() 查看详细信息');
    }
  }, []);

  // 获取系统显示名称
  const getSystemDisplayName = () => {
    return systemInfo?.displayName || '';
  };

  // 处理下载
  const handleDownload = async () => {
    if (!systemInfo?.downloadUrl) return;
    
    setIsDownloading(true);
    
    try {
      // 创建隐藏的下载链接
      const link = document.createElement('a');
      link.href = systemInfo.downloadUrl;
      link.download = systemInfo.fileName;
      link.style.display = 'none';
      
      // 触发下载
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('开始下载:', systemInfo.fileName);
      
      // 模拟下载时间
      setTimeout(() => {
        setIsDownloading(false);
      }, 2000);
      
    } catch (error) {
      console.error('下载失败:', error);
      setIsDownloading(false);
      alert('下载失败，请稍后重试');
    }
  };

  if (!systemInfo) {
    return (
      <div className={`bg-gray-600 text-gray-300 rounded-lg font-medium transition-all duration-300 border border-gray-500 w-full sm:w-auto relative z-10 ${
        variant === 'hero' 
          ? 'px-8 py-4 text-lg font-semibold' 
          : 'px-4 py-2 text-sm'
      }`}>
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-300"></div>
          <span>检测系统中...</span>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={handleDownload}
      disabled={isDownloading}
      className={`bg-gradient-to-r from-purple-600 to-violet-600 text-white hover:from-purple-500 hover:to-violet-500 rounded-lg font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/25 border border-purple-400/20 w-full sm:w-auto relative z-10 disabled:opacity-50 disabled:cursor-not-allowed group ${
        variant === 'hero' 
          ? 'px-8 py-4 text-lg font-semibold' 
          : 'px-4 py-2 text-sm'
      }`}
    >
      {isDownloading ? (
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          <span>下载中...</span>
        </div>
      ) : (
        <div className="flex items-center space-x-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span>
            {variant === 'hero' 
              ? `下载软件 (${getSystemDisplayName()})` 
              : '下载软件'
            }
          </span>
        </div>
      )}
      
      {/* 悬停提示 */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-20">
        {systemInfo.fileName}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
      </div>
    </button>
  );
};

export default DownloadButton;